import type { DragEventHandler, KeyboardEventHandler } from 'react';

export interface DragHandleProps {
  /** Accessible label — what this handle reorders (e.g. "Reorder Status"). */
  'aria-label': string;
  /** When false the handle renders inert (no drag, muted affordance). */
  draggable?: boolean;
  onDragStart?: DragEventHandler<HTMLSpanElement>;
  onDragEnd?: DragEventHandler<HTMLSpanElement>;
  /** Reflected as `data-dragging` for the dimmed-while-dragging style. */
  dragging?: boolean;
  /**
   * Opt-in keyboard reorder: when set the handle becomes focusable and
   * forwards key events (consumers wire ArrowUp/ArrowDown to move the row).
   * Omit to keep the legacy mouse-only behaviour + tab order untouched.
   */
  onKeyDown?: KeyboardEventHandler<HTMLSpanElement>;
  className?: string;
  'data-testid'?: string;
}

/**
 * The `⠿` grab affordance for drag-reorderable rows. A draggable `<span>` — the
 * row it lives in owns the `onDragOver` / `onDrop` drop-target wiring; this
 * primitive is only the drag *source*. `grab` cursor, muted glyph; when
 * `draggable` is false it renders inert (members / archived rows). Matches the
 * Attributes-table + option-editor `⠿` handle in the
 * `settings-document-templates` mock.
 */
export function DragHandle({
  'aria-label': ariaLabel,
  draggable = true,
  onDragStart,
  onDragEnd,
  dragging = false,
  onKeyDown,
  className,
  'data-testid': dataTestId
}: DragHandleProps) {
  const cls = ['pn-drag-handle', className].filter(Boolean).join(' ');
  return (
    <span
      className={cls}
      role="button"
      aria-label={ariaLabel}
      draggable={draggable}
      data-dragging={dragging ? 'true' : 'false'}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onKeyDown={onKeyDown}
      tabIndex={onKeyDown ? 0 : undefined}
      data-testid={dataTestId}
    >
      ⠿
    </span>
  );
}
